import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ArrowDown, Check, Clock, Play, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import { Reveal } from "@/components/motion/Reveal";
import { MaskedLines } from "@/components/motion/MaskedLines";
import { MaturityBadge } from "@/components/MaturityBadge";
import { LEARNOVA, LEARNOVA_EVIDENCE } from "@/config/site";

export default function LearnovaPage() {
  const { t, lang } = useLanguage();
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useSEO({
    title: t.learnova.seo.title,
    description: t.learnova.seo.description,
    path: "/applications/learnova",
    image: { fr: "/og-learnova-fr.png", en: "/og-learnova-en.png" },
  });
  const l = t.learnova;

  return (
    <div data-testid="learnova-page">
      {/* A — HERO */}
      <section className="bg-ink text-sand">
        <div className="container-site py-16 sm:py-24 lg:py-32">
          <p className="eyebrow">{l.hero.eyebrow}</p>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-white">
            <MaskedLines lines={[l.hero.name]} />
          </h1>
          <Reveal delay={0.15}>
            <p className="mt-4 text-sm sm:text-base font-mono uppercase tracking-[0.18em] text-ochre">
              {l.hero.category}
            </p>
            <p className="mt-5 font-display text-2xl sm:text-3xl font-bold text-white max-w-2xl">
              {l.hero.tagline}
            </p>
            <p className="mt-4 max-w-xl text-sand/75 leading-relaxed">{l.hero.description}</p>
            <div className="mt-6">
              <MaturityBadge status="prototype" onDark testId="learnova-badge-prototype" />
              <p className="mt-3 text-xs text-sand/60">{l.hero.maturityNote}</p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={LEARNOVA.prototypeUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="learnova-live-prototype-button"
                className="inline-flex items-center gap-2 rounded-full bg-ochre px-6 py-3 text-sm font-semibold text-ink transition-all duration-200 hover:bg-white hover:shadow-md active:scale-[0.98]"
              >
                {l.hero.ctaPrototype}
                <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href="#demo"
                data-testid="learnova-watch-demo-button"
                className="inline-flex items-center gap-2 rounded-full border border-sand/30 px-6 py-3 text-sm font-semibold text-sand transition-all duration-200 hover:border-ochre hover:text-ochre active:scale-[0.98]"
              >
                <Play className="h-4 w-4" aria-hidden="true" />
                {l.hero.ctaDemo}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* B — THE PROBLEM */}
      <section className="container-site py-16 sm:py-24" aria-labelledby="problem-title">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="eyebrow">{l.problem.eyebrow}</p>
              <h2 id="problem-title" className="mt-3 font-display text-2xl sm:text-3xl font-bold tracking-tight">
                {l.problem.title}
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-8 space-y-5">
            <Reveal delay={0.1}>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">{l.problem.text1}</p>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="text-base sm:text-lg text-foreground font-medium leading-relaxed border-l-2 border-ochre pl-5">
                {l.problem.text2}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* C — APPROACH FLOW */}
      <section className="bg-sand-muted/60 border-y border-border" aria-labelledby="approach-title">
        <div className="container-site py-16 sm:py-24">
          <Reveal>
            <p className="eyebrow">{l.approach.eyebrow}</p>
            <h2 id="approach-title" className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              {l.approach.title}
            </h2>
            <p className="mt-4 max-w-2xl text-muted-foreground">{l.approach.intro}</p>
          </Reveal>
          <ol className="mt-12 relative max-w-3xl">
            <span className="absolute left-[1.15rem] top-2 bottom-2 w-px bg-border" aria-hidden="true" />
            {l.approach.steps.map((step, i) => (
              <Reveal key={step.label} delay={i * 0.06}>
                <li data-testid={`learnova-flow-step-${i + 1}`} className="relative flex gap-6 pb-10 last:pb-0">
                  <span className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink font-mono text-xs font-semibold text-ochre">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="font-display text-lg sm:text-xl font-bold uppercase tracking-wide">
                      {step.label}
                    </h3>
                    <p className="mt-1 text-sm sm:text-base text-muted-foreground leading-relaxed">{step.desc}</p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* D — HOW THE PROTOTYPE WORKS */}
      <section className="container-site py-16 sm:py-24" aria-labelledby="how-title">
        <Reveal>
          <p className="eyebrow">{l.how.eyebrow}</p>
          <h2 id="how-title" className="mt-3 font-display text-2xl sm:text-3xl font-bold tracking-tight">
            {l.how.title}
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">{l.how.intro}</p>
        </Reveal>
        <ol className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {l.how.steps.map((step, i) => (
            <Reveal key={step} delay={i * 0.05}>
              <li className="flex h-full items-start gap-3 rounded-xl border border-border bg-card p-5">
                <span className="font-mono text-xs font-semibold text-ochre mt-1">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <p className="font-semibold text-sm sm:text-base">{step}</p>
                  {i < l.how.steps.length - 1 && (
                    <ArrowDown className="mt-2 h-4 w-4 text-border" aria-hidden="true" />
                  )}
                </div>
              </li>
            </Reveal>
          ))}
        </ol>
      </section>

      {/* E — AVAILABLE NOW vs ROADMAP */}
      <section className="bg-ink text-sand" aria-labelledby="maturity-title">
        <div className="container-site py-16 sm:py-24">
          <Reveal>
            <p className="eyebrow">{l.maturity.eyebrow}</p>
            <h2 id="maturity-title" className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
              {l.maturity.title}
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            <Reveal>
              <div className="h-full rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 sm:p-8" data-testid="learnova-available-now">
                <h3 className="flex items-center gap-2 font-display text-lg font-bold text-emerald-300">
                  <Check className="h-5 w-5" aria-hidden="true" />
                  {l.maturity.nowTitle}
                </h3>
                <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {l.maturity.now.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-sand/85">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6 sm:p-8" data-testid="learnova-roadmap">
                <h3 className="flex items-center gap-2 font-display text-lg font-bold text-amber-300">
                  <Clock className="h-5 w-5" aria-hidden="true" />
                  {l.maturity.roadmapTitle}
                </h3>
                <ul className="mt-5 space-y-3">
                  {l.maturity.roadmap.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-sand/85">
                      <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 border-t border-white/10 pt-4 text-xs text-sand/60 leading-relaxed">
                  {l.maturity.disclaimer}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* F — REAL PRODUCT EVIDENCE (original screenshots supplied by the project owner, unmodified) */}
      {LEARNOVA_EVIDENCE.length > 0 && (
        <section
          className="bg-sand-muted/60 border-y border-border"
          aria-labelledby="evidence-title"
          data-testid="learnova-evidence-gallery"
        >
          <div className="container-site py-16 sm:py-24">
            <Reveal>
              <p className="eyebrow">{l.evidence.eyebrow}</p>
              <h2 id="evidence-title" className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                {l.evidence.title}
              </h2>
              <p className="mt-3 max-w-2xl text-muted-foreground">{l.evidence.subtitle}</p>
            </Reveal>

            <div className="mt-12 space-y-14">
              {LEARNOVA_EVIDENCE.map((src, i) => {
                const step = l.evidence.steps[i];
                return (
                  <Reveal key={src} delay={0.05}>
                    <figure className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
                      <figcaption className={`lg:col-span-4 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                        <span className="font-mono text-xs font-semibold text-ochre">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <h3 className="mt-2 font-display text-lg sm:text-xl font-bold uppercase tracking-wide">
                          {step.title}
                        </h3>
                        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{step.caption}</p>
                      </figcaption>
                      <button
                        type="button"
                        onClick={() => setLightbox(i)}
                        data-testid={`evidence-screenshot-${i + 1}`}
                        aria-label={`${l.evidence.enlarge}: ${step.title}`}
                        className={`lg:col-span-8 ${i % 2 === 1 ? "lg:order-1" : ""} block w-full overflow-hidden rounded-xl border border-border bg-card shadow-sm cursor-zoom-in transition-all duration-300 hover:border-ochre/50 hover:shadow-md`}
                      >
                        <img src={src} alt={step.alt} loading="lazy" className="w-full h-auto" />
                      </button>
                    </figure>
                  </Reveal>
                );
              })}
            </div>

            <Reveal delay={0.1}>
              <div className="mt-14 text-center">
                <a
                  href={LEARNOVA.prototypeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="evidence-live-prototype-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-ochre hover:shadow-md active:scale-[0.98]"
                >
                  {l.evidence.cta}
                  <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </a>
                <p className="mt-4 max-w-2xl mx-auto text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {l.evidence.note}
                </p>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* G — VIDEO DEMONSTRATION */}
      <section id="demo" className="container-site py-16 sm:py-24 scroll-mt-24" aria-labelledby="video-title">
        <Reveal>
          <p className="eyebrow">{l.video.eyebrow}</p>
          <h2 id="video-title" className="mt-3 font-display text-2xl sm:text-3xl font-bold tracking-tight">
            {l.video.title}
          </h2>
          <p className="mt-3 text-sm text-muted-foreground">{l.video.note}</p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-8 overflow-hidden rounded-2xl border border-border bg-ink shadow-lg" data-testid="learnova-demo-video-frame">
            <div className="aspect-video">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${LEARNOVA.youtubeVideoId}`}
                title={l.video.iframeTitle}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="h-full w-full"
              />
            </div>
          </div>
        </Reveal>
      </section>

      {/* H — LIVE PROTOTYPE */}
      <section className="container-site pb-16 sm:pb-24">
        <Reveal>
          <div className="rounded-2xl border-2 border-ochre/40 bg-card p-8 sm:p-12 lg:p-16 text-center">
            <MaturityBadge status="prototype" />
            <h2 className="mt-5 font-display text-2xl sm:text-3xl font-bold tracking-tight">{l.live.title}</h2>
            <p className="mt-4 max-w-xl mx-auto text-muted-foreground">{l.live.text}</p>
            <a
              href={LEARNOVA.prototypeUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="learnova-open-prototype-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-ink px-8 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-ochre hover:shadow-md active:scale-[0.98]"
            >
              {l.live.cta}
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </a>
            <p className="mt-4 text-xs font-mono text-muted-foreground">
              {l.live.urlLabel}: {LEARNOVA.prototypeUrl}
            </p>
          </div>
        </Reveal>
      </section>

      {/* Lightbox — original screenshot at full size, no overlay effects */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            role="dialog"
            aria-modal="true"
            aria-label={l.evidence.steps[lightbox].title}
            data-testid="evidence-lightbox"
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/95 p-4 sm:p-10"
          >
            <button
              type="button"
              onClick={() => setLightbox(null)}
              aria-label={l.evidence.close}
              data-testid="evidence-lightbox-close"
              className="absolute top-4 right-4 inline-flex h-11 w-11 items-center justify-center rounded-full border border-sand/30 text-sand transition-colors duration-200 hover:border-ochre hover:text-ochre"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
            <motion.img
              key={LEARNOVA_EVIDENCE[lightbox]}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              src={LEARNOVA_EVIDENCE[lightbox]}
              alt={l.evidence.steps[lightbox].alt}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[88vh] max-w-full object-contain rounded-lg bg-card"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
